import { getEvents } from '@cannect/services/events'
import { OrderTypes } from '@cannect/types/OrderTypes'
import LoadingContainer from 'components/LoadingContainer'
import { usePrepareCart } from 'hooks/usePrepareCart'
import useQueryString from 'hooks/useQueryString'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from 'services/api'
import useMediaQuery from '../../hooks/useMediaQuery'
import { MOBILE_WIDTH } from '../../utils/constants'
import Step from '../NewCart/Steps'
import PaymentMethods from './PaymentMethod'
import * as S from './styles'

declare global {
  interface Window {
    intervalId: number
  }
}

function NewCartCheckout() {
  const [error, setError] = useState(false)
  const isMobile = useMediaQuery(MOBILE_WIDTH)
  const [address, setAddress] = useState({ shippingAddress: {}, billingAddress: {} })
  const { lastOrderCreated, loading: loadingOrders, getLastOrder, keepRecurrency } = usePrepareCart()
  const [documentList, setDocumentList] = useState<Record<string, any>>({})
  const originCheck = ['acolhimento-cannect', 'B2C', 'COD B', 'Recipe', 'COD C', 'app-cannect', 'agente-autonomo']
  const [loadingContainer, setLoading] = useState(false)
  const history = useHistory()
  const { filters } = useQueryString()
  const cart_id = filters?.cart_id
  const validUrl = cart_id ? lastOrderCreated?.uuid : lastOrderCreated?.id

  const intervalIdRef = useRef<number | null>(null)

  const handleStorageChange = useCallback(
    (e: StorageEvent) => {
      if (e.key === 'selectedProducts' || e.key === '@CANNECT:ORDER_CHECKOUT_CART_ID') {
        getLastOrder()
      }
    },
    [getLastOrder]
  )

  useEffect(() => {
    getLastOrder()

    window.addEventListener('storage', handleStorageChange)

    const interval = setInterval(() => {
      getLastOrder(false)
    }, 10000)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    if (lastOrderCreated && lastOrderCreated.items && lastOrderCreated.items.length === 0) {
      const url = cart_id ? `/carrinho-wa?cart_id=${cart_id}` : `/carrinho`
      toast.info('Seu carrinho foi atualizado, redirecionando para revisá-lo.')
      history.push(url)
    }
  }, [lastOrderCreated])

  useEffect(() => {
    if (cart_id && lastOrderCreated?.cep) {
      const shipping = {
        cep: lastOrderCreated.cep,
        state: lastOrderCreated.state,
        city: lastOrderCreated.city,
        street: lastOrderCreated.street,
        district: lastOrderCreated.district,
        address_number: lastOrderCreated.address_number,
        is_discrete_shipping: lastOrderCreated.is_discrete_shipping ?? false
      }
      setAddress((prev) => ({
        ...prev,
        shippingAddress: lastOrderCreated.shippingAddress || shipping,
        billingAddress: lastOrderCreated.billingAddress || lastOrderCreated.shippingAddress || shipping
      }))
    }
  }, [cart_id, lastOrderCreated?.id, lastOrderCreated?.cep])

  const onFinishPayment = (orderInfos: OrderTypes, paymentMethod: string, paymentStatus?: string) => {
    const itemsIds = orderInfos?.items?.map((item: any) => item.id)
    const eventData = {
      eventName: 'purchase',
      orderId: orderInfos?.id,
      value: orderInfos?.amount,
      items_ids: itemsIds,
      patient_id: orderInfos?.patient_id,
      payment_method: paymentMethod || '',
      coupon_id: orderInfos?.coupon_id,
      payment_status: paymentStatus || ''
    }
    getEvents.eventSendDataLayer(eventData)
  }

  const finishOrderByPaymentMethod = async () => {
    const BASE_PAYMENT_URL = cart_id ? 'order_cart_public' : 'order_cart'
    const { documents, recipes } = lastOrderCreated
    const { shippingAddress, billingAddress } = address

    try {
      setLoading(true)
      const payload = {
        documents: (documents?.flat()?.length > 0 || documents.length > 0) && (documents?.documents_order || documents),
        recipes: [recipes[recipes.length - 1]],
        shippingAddress,
        billingAddress,
        person_id: cart_id && lastOrderCreated?.person?.id,
        // Compra Recorrente: o ciclo não pode cair no último PUT antes do
        // pagamento (FR-013); sem plano ou sem dono no carrinho do link, `{}`.
        ...keepRecurrency()
      }

      const { data } = await api.put(`/${BASE_PAYMENT_URL}/${lastOrderCreated.id}`, payload)

      // const validateAnvisa =
      //   (documentList.ANVISA && documentList.ANVISA.length > 0) ||
      //   (documentList.PROCURACAO && documentList.PROCURACAO.length > 0)

      // if (originCheck.some((origin) => origin === data?.order?.originCheckout) && !validateAnvisa) {
      //   const validator = await api.get(`anvisaAuth/order/${data?.order?.id}`)

      //   if (validator?.data?.success && validator?.data?.message === 'waitingDocs') {
      //     await api.post('anvisaAuth/launchAnvisa', { order_id: data?.order?.id })
      //   } else if (
      //     validator?.data?.success &&
      //     validator?.data?.message === 'inexist' &&
      //     documentList.IDENTIDADE &&
      //     documentList.IDENTIDADE.length > 0
      //   ) {
      //     await api.post('anvisaAuth/clickAutomation', { order_id: data?.order?.id })
      //   }
      // }

      if (data?.success === 'true') {
        if (cart_id && data?.order?.id) {
          history.push(`/detalhes-do-pedido/${data.order.id}?wa=1`)
        } else if (!cart_id) {
          const needsDocuments = !data?.order?.documents || data?.order?.documents.length === 0
          const needsRecipes = !data?.order?.recipes || data?.order?.recipes.length === 0
          if (needsDocuments || needsRecipes) {
            history.push(`/enviar-receita/${data?.order?.id}`)
          }
        }
      }
      return data
    } catch (error) {
      setError(true)
      toast.error('Ocorreu um erro ao finalizar o pedido. Por favor, tente novamente.')
      return { error: true }
    } finally {
      setLoading(false)
    }
  }

  const clearIntervalOutside = () => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }
  }

  const backToCart = () => {
    const url = cart_id ? `/carrinho-wa?cart_id=${cart_id}` : `/carrinho`
    history.push(url)
  }

  if (loadingOrders) return <LoadingContainer loading />

  return (
    <S.Container>
      <Step step={3} isCarrinhoWa={!!cart_id} />

      <S.ContainerResumePay isMobile={isMobile}>
        <PaymentMethods
          loadingContainer={loadingContainer}
          clearIntervalOutside={clearIntervalOutside}
          finishOrderByPaymentMethod={finishOrderByPaymentMethod}
          error={error}
          hasMultipleMethods
        />
      </S.ContainerResumePay>

      <S.ButtonContainer>
        <S.BackCart onClick={backToCart}>Voltar para o inicio</S.BackCart>
      </S.ButtonContainer>
    </S.Container>
  )
}

export default NewCartCheckout
